import React, { useState } from "react";
import {
  Box,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { FONT_INTER } from "../../../styles/fonts";
import { focusRingSx } from "../../../styles/focusRing";
import visuallyHiddenSx from "../../../utils/a11y/visuallyHiddenSx";

const ROWS_PER_PAGE_LABEL_ID = "pagination-rows-per-page-label";

// MUI Tooltip copies a string title into aria-label on its child; undefined overrides that, since
// aria-label is not allowed on plain div/span/p and the visible text or own label already names them
const NO_TOOLTIP_LABEL = { "aria-label": undefined };

// aria-disabled (not disabled) keeps keyboard focus on the button after it reaches the first/last page
const navButtonSx = {
  color: "#0079FD",
  "&:focus-visible": focusRingSx,
  "&[aria-disabled='true']": {
    color: "#B9BDC6",
    pointerEvents: "none",
  },
};

// the live region announces the full sentence; the compact visible text stays hidden from AT
// forwardRef + rest props so it can be a Tooltip child
const PageStatus = React.forwardRef(
  ({ page, totalPages, visibleText, sx, ...other }, ref) => (
    <Typography
      {...other}
      ref={ref}
      component="div"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      sx={sx}
    >
      <span aria-hidden="true">{visibleText}</span>
      <Box component="span" sx={visuallyHiddenSx}>
        Página {page} de {totalPages}
      </Box>
    </Typography>
  )
);

const Pagination = ({
  page = 1,
  totalPages = 1,
  totalItems = 0,
  itemsLabel = "projetos",
  rowsPerPage = 16,
  rowsPerPageOptions = [16, 32, 48],
  onPageChange,
  onRowsPerPageChange,
  compact = false,
}) => {
  const [rowsPerPageTooltipHovered, setRowsPerPageTooltipHovered] =
    useState(false);
  const [rowsPerPageMenuOpen, setRowsPerPageMenuOpen] = useState(false);

  const isFirstPage = page === 1;
  const isLastPage = page === totalPages;

  const handlePreviousPage = () => {
    if (!isFirstPage && onPageChange) {
      onPageChange(page - 1);
    }
  };

  const handleNextPage = () => {
    if (!isLastPage && onPageChange) {
      onPageChange(page + 1);
    }
  };

  const handleRowsPerPageChange = (event) => {
    if (onRowsPerPageChange) {
      onRowsPerPageChange(Number(event.target.value));
    }
  };

  // Compact mode: for narrow containers (e.g. one column of a Miller-columns layout) where the
  // full layout's item count + rows-per-page selector no longer fit on one line and wrap.
  if (compact) {
    return (
      <Box
        component="nav"
        aria-label="Paginação"
        sx={{
          width: "100%",
          minHeight: 48,
          px: 2,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          boxSizing: "border-box",
          bgcolor: "#FFFFFF",
          borderTop: "1px solid #E5E7EB",
        }}
      >
        <Tooltip title={`${totalItems} ${itemsLabel}`}>
          <Typography
            noWrap
            {...NO_TOOLTIP_LABEL}
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 12,
              fontWeight: 600,
              color: "#0079FD",
              minWidth: 0,
            }}
          >
            {totalItems} {itemsLabel}
          </Typography>
        </Tooltip>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
          <Tooltip title="Página anterior">
            <span {...NO_TOOLTIP_LABEL}>
              <IconButton
                aria-label="Página anterior"
                aria-disabled={isFirstPage}
                size="small"
                onClick={handlePreviousPage}
                sx={navButtonSx}
              >
                <ChevronLeftIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>

          <PageStatus
            page={page}
            totalPages={totalPages}
            visibleText={`${page}/${totalPages}`}
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 12,
              fontWeight: 500,
              color: "#252A34",
              whiteSpace: "nowrap",
            }}
          />

          <Tooltip title="Próxima página">
            <span {...NO_TOOLTIP_LABEL}>
              <IconButton
                aria-label="Próxima página"
                aria-disabled={isLastPage}
                size="small"
                onClick={handleNextPage}
                sx={navButtonSx}
              >
                <ChevronRightIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component="nav"
      aria-label="Paginação"
      sx={{
        width: "100%",
        height: 56,
        px: 3,
        display: "grid",
        gridTemplateColumns: "1fr auto 1fr",
        alignItems: "center",
        boxSizing: "border-box",
        bgcolor: "#FFFFFF",
        borderTop: "1px solid #E5E7EB",
      }}
    >
      <Box />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Tooltip title="Página anterior">
          <span {...NO_TOOLTIP_LABEL}>
            <IconButton
              aria-label="Página anterior"
              aria-disabled={isFirstPage}
              onClick={handlePreviousPage}
              sx={navButtonSx}
            >
              <ChevronLeftIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={`Página ${page} de ${totalPages}`}>
          <PageStatus
            {...NO_TOOLTIP_LABEL}
            page={page}
            totalPages={totalPages}
            visibleText={`${page} de ${totalPages}`}
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 14,
              fontWeight: 500,
              color: "#252A34",
              minWidth: 50,
              textAlign: "center",
            }}
          />
        </Tooltip>

        <Tooltip title="Próxima página">
          <span {...NO_TOOLTIP_LABEL}>
            <IconButton
              aria-label="Próxima página"
              aria-disabled={isLastPage}
              onClick={handleNextPage}
              sx={navButtonSx}
            >
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box
        sx={{
          justifySelf: "end",
          display: "flex",
          alignItems: "center",
          gap: 3,
        }}
      >
        <Tooltip title={`${totalItems} ${itemsLabel}`}>
          <Typography
            {...NO_TOOLTIP_LABEL}
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 14,
              fontWeight: 600,
              color: "#0079FD",
            }}
          >
            {totalItems} {itemsLabel}
          </Typography>
        </Tooltip>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography
            component="span"
            id={ROWS_PER_PAGE_LABEL_ID}
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 14,
              color: "#252A34",
              textTransform: "none",
            }}
          >
            Exibir
            <Box component="span" sx={visuallyHiddenSx}>
              {" "}
              itens por página
            </Box>
          </Typography>

          <Tooltip
            title="Quantidade de itens por página"
            disableFocusListener
            open={rowsPerPageTooltipHovered && !rowsPerPageMenuOpen}
            onOpen={() => setRowsPerPageTooltipHovered(true)}
            onClose={() => setRowsPerPageTooltipHovered(false)}
          >
            <Select
              {...NO_TOOLTIP_LABEL}
              id="pagination-rows-per-page"
              labelId={ROWS_PER_PAGE_LABEL_ID}
              size="small"
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
              onOpen={() => setRowsPerPageMenuOpen(true)}
              onClose={() => setRowsPerPageMenuOpen(false)}
              MenuProps={{ PaperProps: { sx: { fontFamily: FONT_INTER } } }}
              sx={{
                width: 110,
                height: 40,
                borderRadius: "8px",
                fontFamily: FONT_INTER,
                fontSize: 14,
                fontWeight: 600,
                color: "#0079FD",

                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                },

                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E2E2E3",
                },

                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E2E2E3",
                },

                "& .MuiSelect-icon": {
                  color: "#0079FD",
                  fontSize: 18,
                },

                "& [role='combobox']:focus-visible": focusRingSx,
              }}
            >
              {rowsPerPageOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

export default Pagination;
